import { useRef, useState } from 'react'
import { startOtp, completeOtp } from '../lib/session'
import { Msg, useT } from '../lib/i18n'
import { ErrorBanner, HintLine } from '../components/ui'
import { Brand } from '../components/Brand'

/**
 * The landing page -- the first screen anyone sees.
 *
 * It used to open with a hard-coded "Transport_Booking" over "Client sign-in"
 * and a blurb about luggage and cartons, which named half the product and
 * none of the feeling. It now opens the way every other screen does since the
 * frakt port: the brand, an eyebrow, one short sentence ending in a full
 * stop, and a subtitle. The mockup has no sign-in screen of its own, so this
 * is its voice applied rather than its layout copied.
 *
 * The flow underneath is untouched. Two things a phone must get right and a
 * desktop can be lazy about:
 *
 *   inputMode="email" / "numeric"  the right keyboard, first time. Typing an
 *                                  email on a numeric-first keyboard is the
 *                                  fastest way to lose someone at step one.
 *   autoComplete="one-time-code"   iOS and Android offer the code from the
 *                                  notification. Without it the user has to
 *                                  leave the app to read their mail.
 *
 * The code field submits itself at six digits. Asking someone to type six
 * digits and then find a button is a step that does not need to exist.
 */
/**
 * `onDone` is passed when this is opened as a sheet over the flow someone is
 * already in (SignInSheet). Without it this is still the standalone screen a
 * dead session falls back to, and that one reloads the app.
 */
export default function SignIn({ onDone }: { onDone?: () => void } = {}) {
  const msg = useT()
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'email' | 'code'>('email')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<unknown>(null)
  const [sentAgain, setSentAgain] = useState(false)
  const codeRef = useRef<HTMLInputElement>(null)

  async function requestCode(again = false) {
    setProblem(null)
    setBusy(true)
    try {
      await startOtp(email)
      setStage('code')
      if (again) setSentAgain(true)
      // The autofocus attribute does not survive the re-render; focusing on
      // the next frame is what actually raises the keyboard.
      requestAnimationFrame(() => codeRef.current?.focus())
    } catch (err) {
      // A send can fail for a reason the code box can still get past --
      // a reserved test domain the relay refuses outright. Show the
      // error, but let a code be entered anyway.
      setProblem(err)
      setStage('code')
      requestAnimationFrame(() => codeRef.current?.focus())
    } finally {
      setBusy(false)
    }
  }

  async function verify(value: string) {
    setProblem(null)
    setBusy(true)
    try {
      await completeOtp(email, value)
      // Opened over a flow: hand control back so the send or the booking the
      // person was in the middle of carries on. Reloading here would throw
      // away the request they had just finished building.
      if (onDone) { onDone(); return }
      // Standalone: straight to Book now, not to the dashboard: arranging
      // transport is what someone opened the app to do, and a summary of past
      // shipments is not a first screen.
      //
      // replace, not assign: a back gesture from Book now must not return to
      // the sign-in screen. A full load rather than a route push, because
      // every cached query was made unauthenticated.
      location.replace('/travel')
    } catch (err) {
      setProblem(err)
      setCode('')
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-y-auto px-6"
         style={{ paddingTop: 'calc(40px + env(safe-area-inset-top,0px))' }}>
      <div className="mx-auto flex w-full max-w-sm flex-1 flex-col">
        {/* The same mark and wordmark as the app bar behind the sign-in, so
            the first screen and the second are visibly one product. */}
        <Brand />

        <header className="mt-7">
          <p className="label">{msg('travel.aLittleCloser')}</p>
          <h1 className="mt-1.5 font-display text-[30px] font-extrabold leading-[1.1] tracking-tight">
            {msg('signIn.clientTitle')}
          </h1>
          <p className="mt-2 text-[15px] leading-relaxed text-ink-3">
            {msg('signIn.clientBlurb')}
          </p>
        </header>

        {stage === 'email' ? (
          <form className="mt-8" onSubmit={(e) => { e.preventDefault(); void requestCode() }}>
            <label className="label mb-2 block" htmlFor="email">{msg('signIn.email')}</label>
            <input
              id="email" className="input" type="email" value={email} required
              inputMode="email" autoComplete="email" autoCapitalize="off"
              autoCorrect="off" spellCheck={false} enterKeyHint="go"
              placeholder={msg('signIn.youExampleCom')}
              onChange={(e) => setEmail(e.target.value)}
            />
            {problem !== null && <div className="-mx-4 mt-3"><ErrorBanner error={problem} /></div>}
            <button type="submit" className="btn-primary mt-5" disabled={busy || !email.trim()}>
              {busy ? msg('common.sending') : msg('signIn.emailMeACode')}
            </button>
          </form>
        ) : (
          <form className="mt-8" onSubmit={(e) => { e.preventDefault(); void verify(code) }}>
            <p className="text-[15px] leading-relaxed text-ink-2">
              <Msg k="signIn.sentCode"
                   nodes={{ email: <strong className="text-ink">{email}</strong> }} />
            </p>

            <label className="label mb-2 mt-6 block" htmlFor="code">{msg('signIn.code')}</label>
            <input
              ref={codeRef}
              id="code"
              className="input text-center font-mono text-[26px] tracking-[0.45em]"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              enterKeyHint="go"
              maxLength={6}
              placeholder="000000"
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 6)
                setCode(v)
                if (v.length === 6) void verify(v)
              }}
            />
            {problem !== null && <div className="-mx-4 mt-3"><ErrorBanner error={problem} /></div>}

            <button type="submit" className="btn-primary mt-5" disabled={busy || code.length < 6}>
              {busy ? msg('common.checking') : msg('signIn.signIn')}
            </button>

            <div className="mt-5 flex items-center justify-between">
              <button type="button" className="btn-bare !px-0"
                      onClick={() => { setStage('email'); setCode(''); setProblem(null) }}>
                {msg('signIn.useAnother')}
              </button>
              <button type="button" className="btn-bare !px-0" disabled={busy || sentAgain}
                      onClick={() => void requestCode(true)}>
                {sentAgain ? msg('signIn.codeSentAgain') : msg('signIn.resendCode')}
              </button>
            </div>
          </form>
        )}

        <p className="mt-8 text-[12.5px] leading-relaxed text-ink-3">
          {msg('signIn.codeHint')}
        </p>

        {/*
          The mockup closes its first screen with a promise rather than a
          control -- a shield and one line. mt-auto pins it to the bottom of
          the viewport on a tall phone and lets it simply follow the content
          on a short one, so it never collides with the keyboard.
        */}
        <div className="mt-auto pb-10 pt-10">
          <HintLine>{msg('app.promise')}</HintLine>
        </div>

      </div>
    </div>
  )
}
